/**
 * useConcierge Hook
 *
 * WebSocket hook for the FlowInCash concierge chat widget.
 * Connects to the Living App Sidecar chat endpoint with:
 * - Automatic connection on mount
 * - Auth token passed via URL query param
 * - Context (route, userId) sent on open
 * - Exponential backoff reconnection
 * - Message queuing when disconnected
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// Use numeric constants — MockWebSocket in tests does not define static WS_* props
const WS_OPEN = 1;

export type ConciergeStatus = 'connecting' | 'connected' | 'disconnected';

export interface ConciergeMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: number;
  data?: unknown;
}

export interface ConciergeAction {
  id: string;
  label: string;
  style: 'primary' | 'secondary' | 'danger';
}

export interface UseConciergeOptions {
  url: string;
  token: string;
  context?: Record<string, unknown>;
  /**
   * When false, the hook does NOT open a WebSocket on mount — it waits for
   * an explicit connect() call. Use this for widgets that are always
   * mounted (e.g. a floating chat bubble rendered on every page) so idle
   * visitors who never open the chat don't hold an always-on connection
   * that auto-reconnects forever and consumes server rate-limit budget.
   * Defaults to true to preserve existing behavior for other callers.
   */
  autoConnect?: boolean;
}

export interface UseConciergeReturn {
  status: ConciergeStatus;
  messages: ConciergeMessage[];
  actions: ConciergeAction[];
  sendMessage: (text: string) => void;
  connect: (context?: Record<string, unknown>) => void;
  disconnect: () => void;
}

export function useConcierge(options: UseConciergeOptions): UseConciergeReturn {
  const { url, token, context: initialContext, autoConnect = true } = options;

  const [status, setStatus] = useState<ConciergeStatus>(autoConnect ? 'connecting' : 'disconnected');
  const [messages, setMessages] = useState<ConciergeMessage[]>([]);
  const [actions, setActions] = useState<ConciergeAction[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const isUnmountedRef = useRef(false);
  const messageQueueRef = useRef<string[]>([]);
  const contextRef = useRef<Record<string, unknown> | undefined>(initialContext);

  // Keep a stable ref to createConnection to avoid stale closures in reconnect callbacks
  const createConnectionRef = useRef<() => void>(() => { /* initialised below */ });

  const createConnection = useCallback(() => {
    if (isUnmountedRef.current) return;

    const wsUrl = `${url}?token=${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    setStatus('connecting');

    ws.onopen = () => {
      if (isUnmountedRef.current) {
        ws.close();
        return;
      }
      reconnectAttemptRef.current = 0;
      setStatus('connected');

      // Send context if available
      if (contextRef.current) {
        ws.send(JSON.stringify({ type: 'context', data: contextRef.current }));
      }

      // Drain queued messages
      const queued = messageQueueRef.current.splice(0);
      for (const payload of queued) {
        ws.send(payload);
      }
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data as string) as Record<string, unknown>;
        const msg: ConciergeMessage = {
          id: typeof data['id'] === 'string' ? data['id'] : `msg-${Date.now()}`,
          role: data['role'] === 'user' ? 'user' : 'assistant',
          text: typeof data['text'] === 'string' ? data['text'] : '',
          timestamp:
            typeof data['timestamp'] === 'number' ? data['timestamp'] : Date.now(),
          data: data['data'],
        };
        setMessages((prev) => [...prev, msg]);
        if (Array.isArray(data['actions'])) {
          setActions(data['actions'] as ConciergeAction[]);
        }
      } catch {
        // Ignore parse errors
      }
    };

    ws.onerror = () => {
      // Close follows error — handled in onclose
    };

    ws.onclose = () => {
      if (isUnmountedRef.current) return;
      wsRef.current = null;
      setStatus('disconnected');

      // Exponential backoff: 1000ms * 2^attempt, capped at 30s
      const attempt = reconnectAttemptRef.current;
      const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
      reconnectAttemptRef.current = attempt + 1;

      reconnectTimeoutRef.current = setTimeout(() => {
        if (!isUnmountedRef.current) {
          createConnectionRef.current();
        }
      }, delay);
    };
  }, [url, token]);

  // Keep ref in sync so reconnect callbacks always call the latest version
  createConnectionRef.current = createConnection;

  /**
   * connect(context?) — starts the WebSocket connection if one isn't already
   * open or pending (used for the lazy/autoConnect=false path: the widget
   * calls this the first time the user opens the chat panel). If already
   * connected, just sends updated route context instead of reconnecting.
   */
  const connect = useCallback((ctx?: Record<string, unknown>) => {
    if (ctx !== undefined) {
      contextRef.current = ctx;
    }
    if (wsRef.current?.readyState === WS_OPEN) {
      if (ctx !== undefined) {
        wsRef.current.send(JSON.stringify({ type: 'context', data: ctx }));
      }
    } else if (!wsRef.current) {
      createConnectionRef.current();
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'user-disconnect');
      wsRef.current = null;
    }
  }, []);

  const sendMessage = useCallback((text: string) => {
    const payload = JSON.stringify({ type: 'message', text, timestamp: Date.now() });
    if (wsRef.current?.readyState === WS_OPEN) {
      wsRef.current.send(payload);
      setMessages((prev) => [
        ...prev,
        {
          id: `user-${Date.now()}`,
          role: 'user' as const,
          text,
          timestamp: Date.now(),
        },
      ]);
    } else {
      messageQueueRef.current.push(payload);
    }
  }, []);

  useEffect(() => {
    isUnmountedRef.current = false;
    if (autoConnect) {
      createConnection();
    }

    return () => {
      isUnmountedRef.current = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [createConnection, autoConnect]);

  return {
    status,
    messages,
    actions,
    sendMessage,
    connect,
    disconnect,
  };
}
